import { type FC, type ReactNode } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import {
  type PimProductV11,
  type PimProductWithVariantsV1,
} from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../../../global-config'
import {
  priceGetFormattedPercentageDiscount,
  priceGetFormattedPrice,
} from '../../../../price'
import {
  type SeoGlobalConfigContextValue,
  SeoHeadTitleMetas,
  useSeoGlobalConfigContext,
} from '../../../../seo'
import { TENANT } from '../../../../tenant'
import {
  type ProductPrices,
  useProductPrices,
} from '../../../components/ProductBuyBox/hooks/useProductPrices'
import { useProductPageContext } from '../../../components/productPageContext'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'

import { getOverriddenMetaTags } from './getOverriddenMetaTags'

const getTitleContent = ({
  intl,
  prices,
  product,
  productTemplate,
  seoConfig,
  tenant,
}: {
  intl: IntlShape
  prices: ProductPrices
  product: PimProductWithVariantsV1
  productTemplate: PRODUCT_TEMPLATES
  seoConfig: SeoGlobalConfigContextValue
  tenant: TENANT
}): string => {
  if (product.meta_title) {
    return getOverriddenMetaTags({
      content: product.meta_title,
      intl,
      prices,
      product,
      productTemplate,
      seoConfig,
    })
  }

  const productName = [
    product.name,
    product.package_size?.quantity,
    product.package_size?.unit,
  ]
    .filter(Boolean)
    .join(' ')

  if (productTemplate === PRODUCT_TEMPLATES.RX && tenant === TENANT.COM) {
    return intl.formatMessage(
      { id: 'metaTag.product.titleRx' },
      {
        productName,
      },
    )
  }

  if (productAccessorIsPrescriptionProduct(product) && tenant === TENANT.CH) {
    return `${product.name} - ${seoConfig.seoShopName}`
  }

  return `${productName} - ${seoConfig.seoShopName}`
}

const getMetaDescription = ({
  intl,
  product,
  productPrices,
  productTemplate,
  seoConfig,
  tenant,
}: {
  intl: IntlShape
  product: PimProductWithVariantsV1
  productPrices: ProductPrices
  productTemplate: PRODUCT_TEMPLATES
  seoConfig: SeoGlobalConfigContextValue
  tenant: TENANT
}): string => {
  if (product.meta_description) {
    /**
     * for details:
     * https://jira.shop-apotheke.com/browse/WSLAM-459
     */
    return getOverriddenMetaTags({
      content: product.meta_description,
      intl,
      prices: productPrices,
      product,
      productTemplate,
      seoConfig,
    })
  }

  /**
   * Redcare doesn't sell prescription products in Swiss directly. It's done by an acquired company.
   * Meta-description should relate to a prescription product even if stock status is
   * out of stock or sale or not in catalog.
   */
  if (productAccessorIsPrescriptionProduct(product) && tenant === TENANT.CH) {
    return intl.formatMessage(
      { id: 'metaTag.product.descriptionRxCH' },
      {
        productName: product.name,
      },
    )
  }

  if (productAccessorIsProductOutOfStock(product.stock_status)) {
    return intl.formatMessage(
      {
        id: 'metaTag.product.description.outOfStock',
      },
      {
        productName: product.name,
      },
    )
  }

  if (
    productAccessorIsProductNotInCatalog(product.stock_status) ||
    productAccessorIsProductOutOfSale(product.stock_status)
  ) {
    return intl.formatMessage(
      {
        id: 'metaTag.product.description.outOfSale',
      },
      {
        productName: product.name,
      },
    )
  }

  const formattedPrice = priceGetFormattedPrice(
    productPrices.displayPrice?.price ?? 0,
    intl,
  )

  const descriptionSet = {
    price: formattedPrice,
    productName: product.name,
    savings: productPrices.discount
      ? priceGetFormattedPercentageDiscount(productPrices.discount)
      : '',
    unit: product.package_size?.unit,
    unitQuantityString: product.package_size?.quantity,
  }

  if (productTemplate === PRODUCT_TEMPLATES.RX && tenant === TENANT.COM) {
    return intl.formatMessage(
      { id: 'metaTag.product.descriptionRx' },
      descriptionSet,
    )
  }

  return productPrices.discount
    ? intl.formatMessage({ id: 'metaTag.product.description' }, descriptionSet)
    : intl.formatMessage(
        {
          id: 'metaTag.product.descriptionNoSavings',
        },
        descriptionSet,
      )
}

const renderAlternateUrls = (currentVariant: PimProductV11): ReactNode => {
  const alternateUrls = currentVariant.alternate_urls ?? {}

  /**
   * if one alternate URL, and same as canonical, do not render
   * for details:
   * https://jira.shop-apotheke.com/browse/WSHOME-694?focusedCommentId=1221752&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-1221752
   */
  const alternateUrlsValues = Object.values(alternateUrls)
  if (
    Object.values(alternateUrls).length === 1 &&
    alternateUrlsValues[0] === currentVariant.canonical_url
  ) {
    return null
  }

  return Object.entries(alternateUrls).map(
    ([urlKey, url]: [urlKey: string, url: string]) => {
      return <link href={url} hrefLang={urlKey} key={urlKey} rel="alternate" />
    },
  )
}

export const ProductPageSeoHeadTags: FC = () => {
  const intl = useIntl()
  const {
    currentPrescriptionType,
    currentVariant,
    offerId,
    product,
    productTemplate,
  } = useProductPageContext()
  const { tenant } = useGlobalConfigContext()
  const seoConfig = useSeoGlobalConfigContext()

  const productPrices = useProductPrices({
    currentPrescriptionType,
    offerId,
    product,
  })

  const descriptionContent = getMetaDescription({
    intl,
    product,
    productPrices,
    productTemplate,
    seoConfig,
    tenant,
  })

  const titleContent = getTitleContent({
    intl,
    prices: productPrices,
    product,
    productTemplate,
    seoConfig,
    tenant,
  })

  return (
    <>
      <SeoHeadTitleMetas title={titleContent} />
      <meta content="index, follow" name="robots" />
      <meta content={product.canonical_url} property="og:url" />
      <link href={product.canonical_url} rel="canonical" />
      <meta content={descriptionContent} name="description" />
      <meta content={descriptionContent} property="twitter:description" />
      <meta content={descriptionContent} property="og:description" />
      {renderAlternateUrls(currentVariant)}
    </>
  )
}
